/* eslint-disable camelcase */
import { Button, Grid, TextField, Typography } from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import React, { useEffect, useState } from 'react'

import BaseFilterModal from '../../../../components/hooks/BaseFilterModal'
import filterStylesComponents from '../../../../assets/filterComponentsStyles'
import colors from '../../../../assets/colors'

import {
  accessQuantity,
  billedQuantity,
  formatAddonDate,
  hasScheduledReduction,
  scheduledQuantity,
} from './addonHelpers'

const useStyles = makeStyles({
  serviceName: {
    fontWeight: 700,
    color: colors.textCustom,
  },
  state: {
    fontSize: '12px',
    color: colors.textGray,
  },
  effect: {
    padding: '10px 12px',
    borderRadius: '8px',
    backgroundColor: colors.lightGray,
    fontSize: '12px',
    color: colors.textPrimary,
    lineHeight: 1.5,
  },
})

// Alterar quantidade tem consequência em dinheiro (aumentar cobra o pró-rata na
// hora) e em acesso (reduzir só vale no fim do ciclo já pago, sem devolução).
// Por isso a alteração passa por confirmação explícita, em vez de um campo que
// dispara a chamada sozinho: o usuário lê o efeito antes de confirmar.
const QuantityDialog = ({
  dialog,
  payInBoleto = false,
  cycleEnd = '',
  isAdmin = false,
  onClose,
  onConfirm,
}) => {
  const classes = useStyles()
  const buttonClasses = filterStylesComponents()
  const [value, setValue] = useState(1)

  const contract = dialog?.contract
  const access = accessQuantity(contract)
  const billed = billedQuantity(contract)
  const scheduled = scheduledQuantity(contract)

  useEffect(() => {
    if (dialog) setValue(billedQuantity(dialog.contract))
  }, [dialog])

  const quantity = Math.max(1, parseInt(value, 10) || 1)
  const unchanged = quantity === billed

  const who = isAdmin ? 'O cliente' : 'Você'
  const changeDate = formatAddonDate(contract?.quantity_change_at)
  const untilDate = changeDate || cycleEnd

  const renderEffect = () => {
    if (unchanged) return null

    // acima da quantidade de acesso: cobra o pró-rata só das unidades novas
    if (quantity > access) {
      const delta = quantity - access

      return (
        <Typography className={classes.effect}>
          {`Liberação imediata. ${
            isAdmin ? 'Será cobrado do cliente' : 'Você paga'
          } agora apenas o valor proporcional aos dias que faltam deste ciclo, de ${delta} ${
            delta === 1 ? 'unidade' : 'unidades'
          }.`}
          {payInBoleto
            ? ' Como o pagamento é em boleto, uma cobrança é gerada com vencimento em 5 dias — o acesso não espera a quitação.'
            : ' A cobrança é feita no cartão na hora; se for recusada, nada muda.'}
          {hasScheduledReduction(contract) &&
            ` A redução agendada para ${scheduled} é cancelada.`}
        </Typography>
      )
    }

    // volta para a quantidade de acesso: só desfaz o agendamento
    if (quantity === access) {
      return (
        <Typography className={classes.effect}>
          {`Desfaz a redução agendada para ${scheduled}: a quantidade segue em ${access}. Nada é cobrado, porque essa quantidade já está paga neste ciclo.`}
        </Typography>
      )
    }

    return (
      <Typography className={classes.effect}>
        {`${who} continua com ${access} ${
          access === 1 ? 'unidade' : 'unidades'
        } até o fim do ciclo já pago${
          untilDate ? ` (${untilDate})` : ''
        }; a próxima fatura já vem com ${quantity}. Não há devolução do valor já pago neste ciclo.`}
      </Typography>
    )
  }

  return (
    <BaseFilterModal
      open={!!dialog}
      title="Alterar quantidade"
      hiddenHelpButton
      handleOpen={onClose}
    >
      <Grid item xs={12} style={{ width: '380px' }}>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Typography className={classes.serviceName}>
              {dialog?.service?.name}
            </Typography>
            <Typography className={classes.state}>
              {hasScheduledReduction(contract)
                ? `Em uso agora: ${access} · próxima fatura: ${billed}${
                    changeDate ? ` (a partir de ${changeDate})` : ''
                  }`
                : `Quantidade atual: ${access}`}
            </Typography>
          </Grid>

          <Grid item xs={12}>
            <TextField
              label="Nova quantidade"
              type="number"
              value={value}
              onChange={e => setValue(e.target.value)}
              onBlur={() => setValue(quantity)}
              fullWidth
              autoFocus
              inputProps={{ min: 1, 'aria-label': 'nova quantidade' }}
            />
          </Grid>

          {!unchanged && (
            <Grid item xs={12}>
              {renderEffect()}
            </Grid>
          )}

          <Grid
            item
            xs={12}
            container
            alignItems="center"
            justifyContent="flex-end"
          >
            <Button
              type="button"
              onClick={onClose}
              className={buttonClasses.btnMoreFilters}
            >
              Voltar
            </Button>

            <Button
              type="button"
              onClick={() => onConfirm(quantity)}
              disabled={unchanged}
              className={buttonClasses.btnAdd}
            >
              Confirmar
            </Button>
          </Grid>
        </Grid>
      </Grid>
    </BaseFilterModal>
  )
}

export default QuantityDialog
